// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN API SERVICE                             ║
// ╚══════════════════════════════════════════════════════════════════╝

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.bank.itsolution.software/api';
const ADMIN_API_URL = `${API_BASE_URL}/admin`;

// Token management
let adminToken = localStorage.getItem('adminToken');
let refreshToken = localStorage.getItem('adminRefreshToken');

const setTokens = (access, refresh) => {
  adminToken = access;
  refreshToken = refresh;
  localStorage.setItem('adminToken', access);
  localStorage.setItem('adminRefreshToken', refresh);
};

const clearTokens = () => {
  adminToken = null;
  refreshToken = null;
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminRefreshToken');
  localStorage.removeItem('adminUser');
};

const getHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  if (adminToken) {
    headers['Authorization'] = `Bearer ${adminToken}`;
  }
  return headers;
};

// API request helper
const apiRequest = async (endpoint, options = {}) => {
  const url = `${ADMIN_API_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getHeaders(),
        ...options.headers,
      },
    });

    // Handle token refresh if needed
    if (response.status === 401 && refreshToken && !options._retry) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return apiRequest(endpoint, { ...options, _retry: true });
      } else {
        clearTokens();
        window.location.href = '/admin/login';
        return { success: false, message: 'Session expired' };
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Admin API Error:', error);
    return { success: false, message: error.message || 'Request failed' };
  }
};

const refreshAccessToken = async () => {
  try {
    const response = await fetch(`${ADMIN_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();
    if (data.success) {
      setTokens(data.data.accessToken, refreshToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

// ============================================================================
// AUTH API
// ============================================================================

export const AdminAuthAPI = {
  login: async (email, password, twoFactorCode) => {
    const res = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, twoFactorCode }),
    });

    if (res.success) {
      setTokens(res.data.accessToken, res.data.refreshToken);
      localStorage.setItem('adminUser', JSON.stringify(res.data.admin));
    }

    return res;
  },

  logout: async () => {
    const res = await apiRequest('/auth/logout', { method: 'POST' });
    clearTokens();
    return res;
  },

  getCurrentAdmin: () => apiRequest('/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    apiRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  getSessions: () => apiRequest('/auth/sessions'),

  revokeSession: (sessionId) =>
    apiRequest(`/auth/sessions/${sessionId}`, { method: 'DELETE' }),

  isAuthenticated: () => !!adminToken,

  getStoredAdmin: () => {
    const stored = localStorage.getItem('adminUser');
    return stored ? JSON.parse(stored) : null;
  },
};

// ============================================================================
// DASHBOARD API
// ============================================================================

export const DashboardAPI = {
  getStats: () => apiRequest('/dashboard/stats'),
  getHealth: () => apiRequest('/dashboard/health'),
  getActivityLog: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/dashboard/activity?${query}`);
  },
};

// ============================================================================
// USERS API (Customers)
// ============================================================================

export const UsersAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/users?${query}`);
  },

  getById: (id) => apiRequest(`/users/${id}`),

  update: (id, data) =>
    apiRequest(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  toggleStatus: (id, status, reason) =>
    apiRequest(`/users/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, reason }),
    }),

  resetPassword: (id, newPassword) =>
    apiRequest(`/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),

  verifyKYC: (id, status, notes) =>
    apiRequest(`/users/${id}/kyc`, {
      method: 'POST',
      body: JSON.stringify({ status, notes }),
    }),
};

// ============================================================================
// ADMIN USERS API
// ============================================================================

export const AdminUsersAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin-users?${query}`);
  },

  create: (data) =>
    apiRequest('/admin-users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id, data) =>
    apiRequest(`/admin-users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id) =>
    apiRequest(`/admin-users/${id}`, { method: 'DELETE' }),

  getRoles: () => apiRequest('/roles'),

  getRolePermissions: (roleId) =>
    apiRequest(`/roles/${roleId}/permissions`),

  updateRolePermissions: (roleId, permissionIds) =>
    apiRequest(`/roles/${roleId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissionIds }),
    }),
};

// ============================================================================
// ACCOUNTS API
// ============================================================================

export const AccountsAdminAPI = {
  getAccounts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/accounts?${query}`);
  },

  getAccount: (id) => apiRequest(`/accounts/${id}`),

  update: (id, data) =>
    apiRequest(`/accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  adjustBalance: (id, data) =>
    apiRequest(`/accounts/${id}/adjust-balance`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  closeAccount: (id, reason) =>
    apiRequest(`/accounts/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  getTypes: () => apiRequest('/accounts/types'),

  saveType: (data, id = null) =>
    apiRequest(id ? `/accounts/types/${id}` : '/accounts/types', {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    }),
};

// ============================================================================
// LOANS API
// ============================================================================

export const LoansAdminAPI = {
  // Products
  getLoanProducts: () => apiRequest('/loans/products'),

  getLoanProduct: (id) => apiRequest(`/loans/products/${id}`),

  saveLoanProduct: (data) =>
    apiRequest(data.id ? `/loans/products/${data.id}` : '/loans/products', {
      method: data.id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    }),

  deleteLoanProduct: (id) =>
    apiRequest(`/loans/products/${id}`, { method: 'DELETE' }),

  // Loans
  getLoans: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/loans?${query}`);
  },

  getLoan: (id) => apiRequest(`/loans/${id}`),

  getPending: () => apiRequest('/loans/pending'),

  approveLoan: (id, data) =>
    apiRequest(`/loans/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),

  rejectLoan: (id, reason) =>
    apiRequest(`/loans/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// ============================================================================
// INVESTMENTS API
// ============================================================================

export const InvestmentsAdminAPI = {
  getStats: () => apiRequest('/investments/stats'),

  getSecurities: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/investments/securities?${query}`);
  },

  getSecurity: (id) => apiRequest(`/investments/securities/${id}`),

  saveSecurity: (data) =>
    apiRequest(data.id ? `/investments/securities/${data.id}` : '/investments/securities', {
      method: data.id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    }),

  updatePrice: (id, currentPrice, previousClose) =>
    apiRequest(`/investments/securities/${id}/price`, {
      method: 'PATCH',
      body: JSON.stringify({ currentPrice, previousClose }),
    }),

  bulkUpdatePrices: (prices) =>
    apiRequest('/investments/securities/bulk-price', {
      method: 'POST',
      body: JSON.stringify({ prices }),
    }),

  deleteSecurity: (id) =>
    apiRequest(`/investments/securities/${id}`, { method: 'DELETE' }),

  // Validate security name against Yahoo Finance
  validateSecurityName: (name, symbol = null) =>
    apiRequest('/investments/securities/validate', {
      method: 'POST',
      body: JSON.stringify({ name, symbol }),
    }),

  // Toggle security data mode (real/simulation)
  toggleDataMode: (id, mode) =>
    apiRequest(`/investments/securities/${id}/toggle-mode`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),

  // Update simulation settings for a security
  updateSimulationSettings: (id, settings) =>
    apiRequest(`/investments/securities/${id}/simulation`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    }),
};

// ============================================================================
// CRYPTO API
// ============================================================================

export const CryptoAdminAPI = {
  getStats: () => apiRequest('/crypto/stats'),

  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/crypto/currencies?${query}`);
  },

  getById: (id) => apiRequest(`/crypto/currencies/${id}`),

  save: (data, id = null) =>
    apiRequest(id ? `/crypto/currencies/${id}` : '/crypto/currencies', {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    }),

  updatePrice: (id, currentPrice) =>
    apiRequest(`/crypto/currencies/${id}/price`, {
      method: 'PATCH',
      body: JSON.stringify({ currentPrice }),
    }),

  bulkUpdatePrices: (prices) =>
    apiRequest('/crypto/currencies/bulk-price', {
      method: 'POST',
      body: JSON.stringify({ prices }),
    }),

  toggleStatus: (id, isActive, isTradeable) =>
    apiRequest(`/crypto/currencies/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive, isTradeable }),
    }),

  delete: (id) =>
    apiRequest(`/crypto/currencies/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// WALLET MANAGEMENT API (HD Wallet Custody)
// ============================================================================

export const WalletAdminAPI = {
  getMasterWallets: () => apiRequest('/wallets/master'),

  initializeMasterWallet: (networkCode) =>
    apiRequest('/wallets/master/initialize', {
      method: 'POST',
      body: JSON.stringify({ networkCode }),
    }),

  getExchangeSummary: () => apiRequest('/wallets/summary'),

  getAllDepositAddresses: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/wallets/addresses?${query}`);
  },

  getNetworksForSetup: () => apiRequest('/wallets/networks'),

  exportPrivateKey: (addressId, reason) =>
    apiRequest('/wallets/export-key', {
      method: 'POST',
      body: JSON.stringify({ addressId, reason }),
    }),

  getAuditLog: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/wallets/audit-log?${query}`);
  },
};

// ============================================================================
// REWARDS API
// ============================================================================

export const RewardsAdminAPI = {
  getStats: () => apiRequest('/rewards/stats'),

  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/rewards?${query}`);
  },

  getById: (id) => apiRequest(`/rewards/${id}`),

  save: (data, id = null) =>
    apiRequest(id ? `/rewards/${id}` : '/rewards', {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    }),

  updateInventory: (id, totalQuantity, remainingQuantity) =>
    apiRequest(`/rewards/${id}/inventory`, {
      method: 'PATCH',
      body: JSON.stringify({ totalQuantity, remainingQuantity }),
    }),

  toggleStatus: (id, status, featured) =>
    apiRequest(`/rewards/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, featured }),
    }),

  delete: (id) =>
    apiRequest(`/rewards/${id}`, { method: 'DELETE' }),

  getRedemptions: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/rewards/redemptions?${query}`);
  },
};

// ============================================================================
// BRANCHES API
// ============================================================================

export const BranchesAdminAPI = {
  getStats: () => apiRequest('/branches/stats'),

  getAvailableManagers: () => apiRequest('/branches/managers'),

  getBranches: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/branches?${query}`);
  },

  getStates: () => apiRequest('/branches/states'),

  getById: (id) => apiRequest(`/branches/${id}`),

  saveBranch: (data) =>
    apiRequest(data.id ? `/branches/${data.id}` : '/branches', {
      method: data.id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    }),

  updateStatus: (id, status) =>
    apiRequest(`/branches/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  assignManager: (id, managerId) =>
    apiRequest(`/branches/${id}/manager`, {
      method: 'PATCH',
      body: JSON.stringify({ managerId }),
    }),

  deleteBranch: (id) =>
    apiRequest(`/branches/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// SETTINGS API
// ============================================================================

export const SettingsAdminAPI = {
  getAll: (category) => {
    const query = category ? `?category=${category}` : '';
    return apiRequest(`/settings${query}`);
  },

  get: (category, key) => apiRequest(`/settings/${category}/${key}`),

  update: (category, key, value) =>
    apiRequest(`/settings/${category}/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  bulkUpdate: (settings) =>
    apiRequest('/settings/bulk', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    }),

  create: (data) =>
    apiRequest('/settings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (category, key) =>
    apiRequest(`/settings/${category}/${key}`, { method: 'DELETE' }),

  // Announcements
  getAnnouncements: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/announcements?${query}`);
  },

  saveAnnouncement: (data, id = null) =>
    apiRequest(id ? `/announcements/${id}` : '/announcements', {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    }),

  deleteAnnouncement: (id) =>
    apiRequest(`/announcements/${id}`, { method: 'DELETE' }),
};

// ============================================================================
// KYC MANAGEMENT API
// ============================================================================

export const KycAdminAPI = {
  // Dashboard & Stats
  getDashboard: () => apiRequest('/kyc/dashboard'),

  // Pending Reviews
  getPendingReviews: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/kyc/pending?${query}`);
  },

  // User KYC Details
  getUserKyc: (userId) => apiRequest(`/kyc/users/${userId}`),

  // Session from Didit
  getSessionFromDidit: (sessionId) => apiRequest(`/kyc/sessions/${sessionId}/didit`),

  // Manual Review Actions
  approveKyc: (userId, data = {}) =>
    apiRequest(`/kyc/users/${userId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  rejectKyc: (userId, reason, notes = '') =>
    apiRequest(`/kyc/users/${userId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason, notes }),
    }),

  resetKyc: (userId, notes = '') =>
    apiRequest(`/kyc/users/${userId}/reset`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),

  // Sessions
  getSessions: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/kyc/sessions?${query}`);
  },

  // Webhook Logs
  getWebhookLogs: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/kyc/webhook-logs?${query}`);
  },

  // Limits Management
  getLimits: () => apiRequest('/kyc/limits'),

  updateLimits: (kycStatus, data) =>
    apiRequest(`/kyc/limits/${kycStatus}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Didit Integration
  updateDiditStatus: (sessionId, newStatus, comment = '') =>
    apiRequest(`/kyc/sessions/${sessionId}/didit-status`, {
      method: 'PATCH',
      body: JSON.stringify({ newStatus, comment }),
    }),

  getPdfUrl: (sessionId) => apiRequest(`/kyc/sessions/${sessionId}/pdf`),

  // Media Downloads
  getSessionMedia: (sessionId) => apiRequest(`/kyc/sessions/${sessionId}/media`),

  getMediaUrl: (sessionId, mediaType) =>
    apiRequest(`/kyc/sessions/${sessionId}/media/${mediaType}`),

  getFullVerificationData: (sessionId) =>
    apiRequest(`/kyc/sessions/${sessionId}/full-data`),

  // Download PDF (returns blob URL for download)
  downloadPdf: async (sessionId) => {
    const url = `${ADMIN_API_URL}/kyc/sessions/${sessionId}/download-pdf`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${adminToken}` },
    });

    if (!response.ok) {
      return { success: false, message: 'Error al descargar PDF' };
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const filename = response.headers.get('Content-Disposition')?.split('filename=')[1]?.replace(/"/g, '') || `KYC_${sessionId}.pdf`;

    return { success: true, data: { url: downloadUrl, filename } };
  },

  // Delete KYC Session
  deleteSession: (sessionId, deleteFromDidit = false) =>
    apiRequest(`/kyc/sessions/${sessionId}`, {
      method: 'DELETE',
      body: JSON.stringify({ deleteFromDidit }),
    }),

  // Delete all KYC data for a user
  deleteUserKyc: (userId, deleteFromDidit = false) =>
    apiRequest(`/kyc/users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify({ deleteFromDidit }),
    }),
};

// ============================================================================
// MARKET SIMULATOR API
// ============================================================================

export const MarketSimulatorAPI = {
  getStatus: () => apiRequest('/market-simulator/status'),

  start: (settings = null) =>
    apiRequest('/market-simulator/start', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    }),

  stop: () =>
    apiRequest('/market-simulator/stop', { method: 'POST' }),

  updateSettings: (settings) =>
    apiRequest('/market-simulator/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),

  triggerEvent: (eventType, magnitude = 1) =>
    apiRequest('/market-simulator/event', {
      method: 'POST',
      body: JSON.stringify({ eventType, magnitude }),
    }),

  resetDaily: () =>
    apiRequest('/market-simulator/reset-daily', { method: 'POST' }),

  setProductPrice: (productId, price) =>
    apiRequest(`/market-simulator/product/${productId}/price`, {
      method: 'PUT',
      body: JSON.stringify({ price }),
    }),

  setProductVolatility: (productId, riskLevel) =>
    apiRequest(`/market-simulator/product/${productId}/volatility`, {
      method: 'PUT',
      body: JSON.stringify({ riskLevel }),
    }),
};

// Export all
export default {
  AdminAuthAPI,
  DashboardAPI,
  UsersAPI,
  AdminUsersAPI,
  AccountsAdminAPI,
  LoansAdminAPI,
  InvestmentsAdminAPI,
  CryptoAdminAPI,
  RewardsAdminAPI,
  BranchesAdminAPI,
  SettingsAdminAPI,
  MarketSimulatorAPI,
  KycAdminAPI,
};
